/**
 * GridRenderer - Unified grid renderer for all layout modes
 *
 * This renderer handles:
 * - Grid mode: X columns × Y rows with row headers
 * - Compare mode (standard): N columns × 1 row with column headers/footers
 * - Compare mode (fanOut): N columns × M rows with row headers + column headers/footers
 * - Gallery mode: N columns × M rows (chunked from single list)
 *
 * All modes render tiles in a grid structure with:
 * - Column headers (fixed at top, if enabled)
 * - Content rows (scrollable area with row headers/content/footers)
 * - Column footers (fixed at bottom, if enabled)
 */

import {THEME} from '@wandb/weave/common/css/color.styles';
import React from 'react';
import {Card} from 'semantic-ui-react';
import {twMerge} from 'tailwind-merge';

import {getModeConfig} from '../../modes/getModeConfig';
import {useIsCompareSynced} from '../../zoom/useIsCompareSynced';
import {LAYOUT_CONSTANTS} from '../constants';
import {
  ColumnFooterSlot,
  ColumnHeaderSlot,
  RowFooterSlot,
  RowHeaderSlot,
} from '../slots';
import {type GridCell, type GridRow} from '../types';
import {
  applyPaginationToRows,
  doCellsHaveCaptions,
  expandCellsWithMasks,
  getCardGroupClassName,
  getColumnCountFromConfig,
  getGridTemplateColumnsWithHeaders,
  getGridTemplateRowsWithHeaders,
  getShowActualSizeFromConfig,
  mediaTypeFillsHeight,
} from './rendererUtils';
import {TileRenderer} from './TileRenderer';
import {
  type ColumnFooterRowProps,
  type ColumnHeaderRowProps,
  type ContentRowData,
  type GridRendererProps,
  type TileProps,
} from './types';

const ColumnHeaderRow: React.FC<ColumnHeaderRowProps> = ({
  gridRow,
  columnCount,
  showRowHeaders,
  firstContentRow,
  columnHeaderControls,
  slotProps,
  allContentRows,
}) => {
  const isCompareMode = slotProps.config.mode === 'compare';

  return (
    <>
      {showRowHeaders && (
        <div
          className={`${isCompareMode ? 'box-border border-r border-wb-border' : ''}`}
          style={{
            gridRow,
            gridColumn: 1,
            minWidth: LAYOUT_CONSTANTS.ROW_HEADER_WIDTH,
            width: LAYOUT_CONSTANTS.ROW_HEADER_WIDTH,
          }}
        />
      )}
      {Array.from({length: columnCount}, (_, colIdx) => {
        const columnCells = firstContentRow?.columns[colIdx] || [];
        const columnCell =
          columnCells[0] || firstContentRow?.row.cellGroups[0]?.[0];

        // Skip this check in compare mode so column controls (run selector, etc.)
        // stay visible even when the current page has no data for a column.
        if (!isCompareMode) {
          const isColumnEmpty = allContentRows.every(contentRow => {
            const cells = contentRow.columns[colIdx] || [];
            return cells.every(cell => cell.isEmpty === true);
          });

          if (isColumnEmpty) {
            return null;
          }
        }

        return (
          <div
            key={`col-header-${colIdx}`}
            className={twMerge(
              isCompareMode && 'px-4 py-8 box-border',
              // Left border only for columns after the first (row header has border-r)
              isCompareMode && colIdx >= 1 && 'border-l border-wb-border'
            )}
            style={{
              gridRow,
              gridColumn: colIdx + (showRowHeaders ? 2 : 1),
              minWidth: 0,
            }}>
            <ColumnHeaderSlot
              controls={columnHeaderControls}
              columnIndex={colIdx}
              cell={columnCell}
              disableRunLinks={slotProps.disableRunLinks}
              config={slotProps.config}
              updateConfig={slotProps.updateConfig}
              stepMeta={slotProps.stepMeta}
              mediaSliderKey={slotProps.mediaSliderKey}
              mediaType={slotProps.mediaType}
              isMaxStepLoading={slotProps.isMaxStepLoading}
              mediaBrowserSize={slotProps.mediaBrowserSize}
              gridData={slotProps.gridData}
              isFullScreenPanelPage={slotProps.isFullScreenPanelPage}
            />
          </div>
        );
      })}
    </>
  );
};

const ColumnFooterRow: React.FC<ColumnFooterRowProps> = ({
  gridRow,
  columnCount,
  showRowHeaders,
  firstContentRow,
  allContentRows,
  columnFooterControls,
  slotProps,
}) => {
  const isCompareMode = slotProps.config.mode === 'compare';

  return (
    <>
      {showRowHeaders && (
        <div
          className={`${isCompareMode ? 'box-border border-r border-wb-border' : ''}`}
          style={{
            gridRow,
            gridColumn: 1,
            minWidth: LAYOUT_CONSTANTS.ROW_HEADER_WIDTH,
            width: LAYOUT_CONSTANTS.ROW_HEADER_WIDTH,
          }}
        />
      )}
      {Array.from({length: columnCount}, (_, colIdx) => {
        const columnCells = firstContentRow?.columns[colIdx] || [];
        const columnCell =
          columnCells[0] || firstContentRow?.row.cellGroups[0]?.[0];
        // Check if this column is entirely empty across all rows - if so, don't render a footer
        const isColumnEmpty = allContentRows.every(contentRow => {
          const cells = contentRow.columns[colIdx] || [];
          // Column is empty if it has no cells or all cells are empty
          return cells.every(cell => cell.isEmpty === true);
        });

        if (isColumnEmpty) {
          return null;
        }

        return (
          <div
            key={`col-footer-${colIdx}`}
            style={{
              gridRow,
              gridColumn: colIdx + (showRowHeaders ? 2 : 1),
              minWidth: 0,
            }}>
            <ColumnFooterSlot
              controls={columnFooterControls}
              columnIndex={colIdx}
              cell={columnCell}
              config={slotProps.config}
              updateConfig={slotProps.updateConfig}
              stepMeta={slotProps.stepMeta}
              mediaSliderKey={slotProps.mediaSliderKey}
              mediaType={slotProps.mediaType}
              isMaxStepLoading={slotProps.isMaxStepLoading}
              mediaBrowserSize={slotProps.mediaBrowserSize}
              gridData={slotProps.gridData}
            />
          </div>
        );
      })}
    </>
  );
};

const RowHeaderRow: React.FC<{
  rowIndex: number;
  row: GridRow;
  rowHeaderControls: any[];
  disableRunLinks?: boolean;
  config: GridRendererProps['config'];
  updateConfig: GridRendererProps['updateConfig'];
  stepMeta: GridRendererProps['stepMeta'];
  mediaSliderKey: GridRendererProps['mediaSliderKey'];
  cardHeight: number;
}> = ({
  rowIndex,
  row,
  rowHeaderControls,
  disableRunLinks,
  config,
  updateConfig,
  stepMeta,
  mediaSliderKey,
  cardHeight,
}) => {
  // gridRowIndex is rowIndex + 1 because content rows start at grid row 1
  // (row 0 is reserved for column headers if they exist)
  const gridRowIndex = rowIndex + 1;
  const isCompareMode = config.mode === 'compare';

  return (
    <div
      className={twMerge(
        'min-h-0 w-full h-full flex flex-1 items-center justify-center',
        isCompareMode && 'box-border border-r border-wb-border',
        isCompareMode && gridRowIndex > 1 && 'border-t border-wb-border'
      )}
      style={{
        gridRow: gridRowIndex,
        gridColumn: 1,
        minWidth: LAYOUT_CONSTANTS.ROW_HEADER_WIDTH,
        maxWidth: LAYOUT_CONSTANTS.ROW_HEADER_WIDTH,
        width: LAYOUT_CONSTANTS.ROW_HEADER_WIDTH,
      }}>
      <div
        /**
         * Text rotation components:
         * - rotate-180 flips text so it reads left-to-right from bottom to top
         * - writingMode: 'vertical-rl' renders text veritcally in the element
         * - direction: 'rtl' ensures truncation clips label before value
         */
        className="truncate rotate-180 flex flex-1 min-w-0 min-h-0 w-full h-full items-center justify-center text-center"
        style={{
          writingMode: 'vertical-rl',
          // Constrain the text to the media height so it doesn't expand the
          // grid row beyond what the media cells need. With writingMode
          // vertical-rl, CSS max-height maps to the vertical screen extent of
          // the text; overflow-hidden + truncate then clip and ellipsize it.
          maxHeight: cardHeight,
        }}>
        <RowHeaderSlot
          controls={rowHeaderControls}
          row={row}
          rowIndex={rowIndex}
          disableRunLinks={disableRunLinks}
          config={config}
          updateConfig={updateConfig}
          stepMeta={stepMeta}
          mediaSliderKey={mediaSliderKey}
        />
      </div>
    </div>
  );
};

const RowContentRow: React.FC<{
  config: GridRendererProps['config'];
  gridRowIndex: number;
  rowIndex: number;
  showRowHeaders: boolean;
  columns: GridCell[][];
  columnCount: number;
  tileHeaderControls: any[];
  tileFooterControls: any[];
  tileProps: TileProps;
  stepMeta: GridRendererProps['stepMeta'];
  isCompareSynced: boolean;
  isFirstRow?: boolean;
  isLastRow?: boolean;
}> = ({
  config,
  gridRowIndex,
  rowIndex,
  showRowHeaders,
  columns,
  tileHeaderControls,
  tileFooterControls,
  tileProps,
  stepMeta,
  isCompareSynced: isSynced,
}) => {
  const showActualSize = getShowActualSizeFromConfig(
    tileProps.config,
    tileProps.mediaType
  );

  // Create transparency grid background style
  const transparencyGridStyle: React.CSSProperties =
    config.mode === 'compare'
      ? {
          backgroundImage: `
      linear-gradient(45deg, light-dark(var(--moon-100), var(--moon-800)) 25%, transparent 25%),
      linear-gradient(-45deg, light-dark(var(--moon-100), var(--moon-800)) 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, light-dark(var(--moon-100), var(--moon-800)) 75%),
      linear-gradient(-45deg, transparent 75%, light-dark(var(--moon-100), var(--moon-800)) 75%)
    `,
          backgroundColor: THEME.bgSurface,
          backgroundSize: '10px 10px',
          backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
        }
      : {};

  const isCompareMode = config.mode === 'compare';
  const rowHasCaptions = doCellsHaveCaptions(columns.flat());
  const footerControls = rowHasCaptions ? tileFooterControls : [];

  const contentSizedTiles = getModeConfig(config.mode).shouldSizeTilesByContent(
    config,
    tileProps.mediaType
  );
  const cardGroupSizingClasses = contentSizedTiles
    ? 'w-auto h-auto'
    : 'w-full h-full';
  const cellSizingClasses = contentSizedTiles
    ? 'flex-none w-auto'
    : 'flex-1 w-full';
  return (
    <>
      {columns.map((columnCells, colIdx) => {
        // Check if this column has captions for conditional border styling
        const hasFanOutVariable =
          config?.compareSettings?.fanOutVariable !== undefined;
        return (
          <div
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={`row-${rowIndex}-col-${colIdx}`}
            className={twMerge(
              'min-w-0 flex items-stretch box-border',
              isSynced && 'min-h-0',
              // with a single row in compare mode, center the content
              isCompareMode && !hasFanOutVariable && 'items-center',
              // isCompareMode && 'px-4',
              // Explicit borders instead of divide: left border for columns after first, top border for rows after first
              isCompareMode && colIdx >= 1 && 'border-l border-wb-border',
              isCompareMode && gridRowIndex > 1 && 'border-t border-wb-border'
            )}
            style={{
              gridRow: gridRowIndex,
              gridColumn: colIdx + (showRowHeaders ? 2 : 1),
            }}>
            <Card.Group
              className={getCardGroupClassName(
                tileProps.config,
                tileProps.mediaType,
                tileProps.shrunk,
                showActualSize,
                `justify-items-center items-center min-w-0 flex flex-col gap-2 ${cardGroupSizingClasses}`
              )}>
              {columnCells.map((cell, cellIdx) => {
                const tileIndex = rowIndex * 1000 + colIdx * 100 + cellIdx;
                const backgroundStyle =
                  cell.isEmpty || cell.isFanOutStepNotFound || !cell.hasMetadata
                    ? {backgroundColor: THEME.bgSurface}
                    : transparencyGridStyle;
                return (
                  <div
                    // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                    key={`col-${colIdx}-cell-${cellIdx}`}
                    className={twMerge(
                      'flex flex-col items-center justify-center',
                      cellSizingClasses,
                      isSynced && 'min-h-0'
                    )}
                    style={{
                      ...backgroundStyle,
                    }}>
                    <TileRenderer
                      cell={cell}
                      isCompareSynced={isSynced}
                      tileIndex={tileIndex}
                      cardWidth={tileProps.cardWidth}
                      cardHeight={tileProps.cardHeight}
                      config={tileProps.config}
                      mediaType={tileProps.mediaType}
                      mediaSliderKey={tileProps.mediaSliderKey}
                      panelRef={tileProps.panelRef}
                      disableRunLinks={tileProps.disableRunLinks}
                      customRunNames={tileProps.customRunNames}
                      stepMeta={stepMeta}
                      updateConfig={tileProps.updateConfig}
                      tileHeaderControls={tileHeaderControls}
                      tileFooterControls={footerControls}
                      columnIndex={colIdx}
                      rowIndex={rowIndex}
                    />
                  </div>
                );
              })}
            </Card.Group>
          </div>
        );
      })}
    </>
  );
};

const RowFooterRow: React.FC<{
  gridRowIndex: number;
  showRowHeaders: boolean;
  columnCount: number;
  row: GridRow;
  rowFooterControls: any[];
  config: GridRendererProps['config'];
  updateConfig: GridRendererProps['updateConfig'];
  stepMeta: GridRendererProps['stepMeta'];
  mediaSliderKey: GridRendererProps['mediaSliderKey'];
}> = ({
  gridRowIndex,
  showRowHeaders,
  columnCount,
  row,
  rowFooterControls,
  config,
  updateConfig,
  stepMeta,
  mediaSliderKey,
}) => {
  return (
    <div
      className={twMerge(
        'flex items-center box-border',
        config.mode === 'compare' &&
          gridRowIndex > 1 &&
          'border-t border-wb-border'
      )}
      style={{
        gridRow: gridRowIndex,
        gridColumn: columnCount + (showRowHeaders ? 2 : 1),
      }}>
      <RowFooterSlot
        controls={rowFooterControls}
        row={row}
        config={config}
        updateConfig={updateConfig}
        stepMeta={stepMeta}
        mediaSliderKey={mediaSliderKey}
      />
    </div>
  );
};

/**
 * Main Grid Renderer - handles all layout modes.
 *
 * @param props - GridRendererProps
 * @returns React element with unified grid structure
 */
export const GridRenderer: React.FC<GridRendererProps> = ({
  gridData,
  slots,
  config,
  updateConfig,
  stepMeta,
  isMaxStepLoading,
  mediaBrowserSize,
  cardWidth,
  cardHeight,
  mediaType,
  mediaSliderKey,
  panelRef,
  disableRunLinks,
  customRunNames,
  shrunk,
  allMasks,
  tilePagination,
  isFullScreenPanelPage = false,
}) => {
  // Evaluated once per panel: per-tile evaluation subscribes every tile to
  // the flag/report contexts and multiplies report-page re-renders.
  const isCompareSynced = useIsCompareSynced({config, mediaType});
  const columnCount = getColumnCountFromConfig(config);

  const tileHeaderControls = slots.tileHeader || [];
  const tileFooterControls = slots.tileFooter || [];
  const columnHeaderControls = slots.columnHeader || [];
  const columnFooterControls = slots.columnFooter || [];
  const rowHeaderControls = slots.rowHeader || [];
  const rowFooterControls = slots.rowFooter || [];
  const contentFooterControls = slots.contentFooter || [];

  if (gridData.rows.length === 0) {
    return <div className="flex w-full flex-col">No data to display</div>;
  }

  // Apply pagination if provided (for gallery and compare modes)
  const rowsToProcess = tilePagination
    ? applyPaginationToRows(gridData.rows, tilePagination, columnCount)
    : gridData.rows;

  // Expand cells with masks - returns grouped cells (each group stacks vertically)
  const expandedRows = rowsToProcess.map(
    (row: {cells: GridCell[]; rowKey?: string}) => ({
      ...row,
      cellGroups: expandCellsWithMasks(row.cells, config, allMasks),
    })
  );

  const hasColumnHeaders = columnHeaderControls.length > 0;
  const hasColumnFooters = columnFooterControls.length > 0;
  const hasRowHeaders = rowHeaderControls.length > 0;
  const hasRowFooters = rowFooterControls.length > 0;

  // Standard structure contract: Each row is a visual row with cells ordered left-to-right
  // GridRenderer expands cells with masks, keeping mask variants grouped together
  // Each cell group maps 1:1 to a column (layouts already chunk rows to columnCount)
  // Within each column, all mask variants stack vertically
  // Layouts are responsible for structuring data correctly:
  // - GalleryLayout: chunks cells into visual rows (each row has up to columnCount cells)
  // - GridLayout: creates rows with cells in left-to-right order
  // - CompareLayout: creates rows with cells ordered left-to-right (one cell per column per row)
  const actualColumnCount = columnCount;
  const contentRows = expandedRows.map(
    (row: {cellGroups: GridCell[][]; rowKey?: string}) => ({
      row,
      columns: row.cellGroups, // Each cell group becomes one column (1:1 mapping)
    })
  );

  const contentRowCount = contentRows.length;

  // Generate grid template columns (same for all grids - headers, content, footers)
  const gridTemplateColumns = getGridTemplateColumnsWithHeaders(
    actualColumnCount,
    hasRowHeaders,
    hasRowFooters,
    config.mode === 'compare' && isFullScreenPanelPage
  );

  // Row-height management is needed only where the mode constrains row heights
  // (see mode config) AND the media type fills height (see mediaTypeFillsHeight),
  // so height-filling media isn't squished. We express it as a cap on visible rows:
  // - workspace: 1 row fills the panel, the content area scrolls for the rest.
  // - full screen: up to MAX_VISIBLE_GRID_ROWS show before scrolling.
  // Width-sized media (image, object3D, etc.) keep the 1fr split.
  const isConstrainedGrid =
    getModeConfig(config.mode).constrainsContentRowHeight &&
    mediaTypeFillsHeight(mediaType);
  const maxRowsForView = isFullScreenPanelPage
    ? LAYOUT_CONSTANTS.MAX_VISIBLE_GRID_ROWS
    : 1;
  const maxVisibleRows = isConstrainedGrid ? maxRowsForView : undefined;

  // Grid template rows for content area only (no headers/footers)
  const contentGridTemplateRows = getGridTemplateRowsWithHeaders(
    contentRowCount,
    {
      contentRowFillsPanel: gridData.metadata.contentRowFillsPanel,
      maxVisibleRows,
    }
  );

  // Prepare content rows data with grid row indices
  const contentRowsData: ContentRowData[] = contentRows.map(
    (
      contentRow: {
        row: {cellGroups: GridCell[][]; rowKey?: string};
        columns: GridCell[][];
      },
      rowIdx: number
    ) => ({
      contentRow,
      gridRowIndex: rowIdx + 1, // Content rows start at 1
      rowIndex: rowIdx,
      hasCaptions: doCellsHaveCaptions(contentRow.columns.flat()),
    })
  );
  const hasCaptions = contentRowsData.some(row => row.hasCaptions);
  const hasColumnFootersOtherThanCaptions =
    hasColumnFooters &&
    columnFooterControls.some(control => control.control !== 'caption');
  const shouldRenderColumnFooters =
    hasColumnFootersOtherThanCaptions ||
    (hasCaptions && contentFooterControls.length === 0);

  const isCompareMode = config.mode === 'compare';
  // Content-sized layout: one wrapping flex container that packs tiles from the
  // top-left so each keeps its own size (items-start: no cross-axis stretch;
  // content-start: no extra space between wrapped rows). Otherwise a fixed grid.
  const layout = getModeConfig(config.mode).shouldSizeTilesByContent(
    config,
    mediaType
  )
    ? 'flex flex-wrap items-start content-start'
    : 'grid';

  return (
    <div className="flex flex-col w-full h-full min-w-0 min-h-0 text-sm leading-tight text-wb-text-tertiary">
      {hasColumnHeaders && (
        <div
          className={`w-full flex-shrink-0 ${layout} ${isCompareMode ? 'box-border border-x border-b border-wb-border' : ''}`}
          style={{
            ...gridTemplateColumns,
            gridTemplateRows: 'auto',
            columnGap: isCompareMode
              ? 0
              : LAYOUT_CONSTANTS.SPACE_BETWEEN_COLUMNS,
          }}>
          <ColumnHeaderRow
            gridRow={1}
            columnCount={actualColumnCount}
            showRowHeaders={hasRowHeaders}
            firstContentRow={contentRows[0]}
            allContentRows={contentRows}
            columnHeaderControls={columnHeaderControls}
            slotProps={{
              disableRunLinks,
              config,
              updateConfig,
              stepMeta,
              mediaSliderKey,
              mediaType,
              isMaxStepLoading,
              mediaBrowserSize,
              gridData,
              isFullScreenPanelPage,
            }}
          />
        </div>
      )}

      <div
        data-testid="grid-renderer-content-grid"
        className={`flex-1 min-h-0 overflow-y-auto w-full ${layout} ${isCompareMode ? 'border-x border-b border-wb-border' : ''}`}
        style={{
          ...gridTemplateColumns,
          ...contentGridTemplateRows,
          gridRowGap:
            config.mode === 'compare' ? 0 : LAYOUT_CONSTANTS.SPACE_BETWEEN_ROWS,
          gridColumnGap:
            config.mode === 'compare'
              ? 0
              : LAYOUT_CONSTANTS.SPACE_BETWEEN_COLUMNS,
        }}>
        {contentRowsData.map(({contentRow, gridRowIndex, rowIndex}, idx) => {
          const rowKey = contentRow.row.rowKey || `row-${rowIndex}`;
          // Cast to GridRow - RowHeaderRow only uses metadata like rowKey, not cells
          const gridRow = {
            ...contentRow.row,
            rowKey,
          } as unknown as GridRow;
          const isFirstRow = idx === 0;
          const isLastRow = idx === contentRowsData.length - 1;

          return (
            <React.Fragment key={rowKey}>
              {hasRowHeaders && (
                <RowHeaderRow
                  rowIndex={rowIndex}
                  row={gridRow}
                  rowHeaderControls={rowHeaderControls}
                  disableRunLinks={disableRunLinks}
                  config={config}
                  updateConfig={updateConfig}
                  stepMeta={stepMeta}
                  mediaSliderKey={mediaSliderKey}
                  cardHeight={cardHeight}
                />
              )}

              <RowContentRow
                config={config}
                isCompareSynced={isCompareSynced}
                gridRowIndex={gridRowIndex}
                rowIndex={rowIndex}
                showRowHeaders={hasRowHeaders}
                columns={contentRow.columns}
                columnCount={actualColumnCount}
                tileHeaderControls={tileHeaderControls}
                tileFooterControls={tileFooterControls}
                tileProps={{
                  config,
                  mediaType,
                  cardWidth,
                  cardHeight,
                  mediaSliderKey,
                  panelRef,
                  disableRunLinks,
                  customRunNames,
                  shrunk,
                  updateConfig,
                  stepMeta,
                }}
                stepMeta={stepMeta}
                isFirstRow={isFirstRow}
                isLastRow={isLastRow}
              />

              {hasRowFooters && (
                <RowFooterRow
                  gridRowIndex={gridRowIndex}
                  showRowHeaders={hasRowHeaders}
                  columnCount={actualColumnCount}
                  row={gridRow}
                  rowFooterControls={rowFooterControls}
                  config={config}
                  updateConfig={updateConfig}
                  stepMeta={stepMeta}
                  mediaSliderKey={mediaSliderKey}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {shouldRenderColumnFooters && (
        <div
          data-testid="grid-renderer-column-footers"
          className={twMerge(
            'w-full flex-shrink-0',
            layout,
            // external borders
            isCompareMode && 'box-border border-x border-b border-wb-border',
            // internal borders
            isCompareMode && 'divide-x divide-wb-border'
          )}
          style={{
            ...gridTemplateColumns,
            gridTemplateRows: 'auto',
            columnGap:
              config.mode === 'compare'
                ? 0
                : LAYOUT_CONSTANTS.SPACE_BETWEEN_COLUMNS,
          }}>
          <ColumnFooterRow
            gridRow={1}
            columnCount={actualColumnCount}
            showRowHeaders={hasRowHeaders}
            firstContentRow={contentRows[0]}
            allContentRows={contentRows}
            columnFooterControls={columnFooterControls}
            slotProps={{
              config,
              updateConfig,
              stepMeta,
              mediaSliderKey,
              mediaType,
              isMaxStepLoading,
              mediaBrowserSize,
              gridData,
            }}
          />
        </div>
      )}
    </div>
  );
};
